import React, { useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import './StepHeader.css';

/** Portal V2 only — short guided copy from classic step title (V1 unchanged). */
function getPortalV2VideoCopy(title) {
    const t = String(title || '').toUpperCase();
    if (t.includes('TOP 7')) {
        return {
            lead: 'Watch the Top 7 intro',
            sublead:
                'This short video prepares you for the next part of Step 2.',
        };
    }
    if (t.includes('OOLA ONE')) {
        return {
            lead: 'Watch the Oola One intro',
            sublead: 'A quick guide before you choose your priority goal.',
        };
    }
    if (t.includes('STEP 3') || t.includes('ACTION')) {
        return {
            lead: 'Watch this short video to continue.',
            sublead: 'See how daily actions connect to your Top 7.',
        };
    }
    if (t.includes('STEP 2')) {
        return {
            lead: 'Watch this short video to continue.',
            sublead: 'This prepares you for setting your goals.',
        };
    }
    return {
        lead: 'Watch this short video to continue.',
        sublead: null,
    };
}

/** Normalize Vimeo URLs for ReactPlayer (expects vimeo.com/ID, not embed). */
function normalizeVimeoUrl(url) {
    if (!url) return url;
    const match = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? `https://vimeo.com/${match[1]}` : url;
}

const StepHeaders = ({ title, url, mobile, changePage, portalV2 }) => {
    const videoUrl = normalizeVimeoUrl(url);
    const [activeButton, ChangeActiveButton] = useState(false);
    const [show, Changeshow] = useState(true);
    const [started, setStarted] = useState(false);
    const [playing, setPlaying] = useState(false);
    const didAdvance = useRef(false);
    const userStartedRef = useRef(false);
    const watchMetaRef = useRef({
        duration: 0,
        maxSeconds: 0,
        startedAt: 0,
    });

    const premium = Boolean(portalV2);

    const goNext = () => {
        if (didAdvance.current) return;
        didAdvance.current = true;
        ChangeActiveButton(true);
        changePage();
        setTimeout(function () {
            Changeshow(false);
        }, 1000);
    };

    const handleStart = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (userStartedRef.current || didAdvance.current) return;
        userStartedRef.current = true;
        watchMetaRef.current.maxSeconds = 0;
        watchMetaRef.current.startedAt = Date.now();
        setStarted(true);
        setPlaying(true);
        ChangeActiveButton(true);
    };

    const handleDuration = (duration) => {
        watchMetaRef.current.duration = Number(duration) || 0;
    };

    const handleProgress = ({ playedSeconds }) => {
        if (!userStartedRef.current) return;
        const seconds = Number(playedSeconds) || 0;
        if (seconds <= 0 || seconds > 86400) return;
        watchMetaRef.current.maxSeconds = Math.max(
            watchMetaRef.current.maxSeconds,
            seconds
        );
    };

    const handleEnded = () => {
        if (!userStartedRef.current || didAdvance.current) return;
        const { duration, maxSeconds, startedAt } = watchMetaRef.current;
        const wallMs = Date.now() - startedAt;
        if (wallMs < 4000) return;
        if (duration < 1) return;
        const requiredSeconds = Math.max(
            Math.min(duration * 0.85, duration - 0.5),
            3
        );
        if (maxSeconds < requiredSeconds) return;
        finishAndAdvance();
    };

    const handlePlayerPlay = () => {
        if (!userStartedRef.current) {
            setPlaying(false);
            return;
        }
        setPlaying(true);
    };

    const finishAndAdvance = () => {
        if (didAdvance.current) return;
        didAdvance.current = true;
        ChangeActiveButton(true);
        changePage();
        setTimeout(function () {
            Changeshow(false);
        }, 1000);
    };

    function changePlay() {
        if (didAdvance.current) return;
        if (premium) {
            goNext();
            return;
        }
        if (!activeButton) return;
        finishAndAdvance();
    }

    if (premium) {
        const { lead, sublead } = getPortalV2VideoCopy(title);

        return (
            <div className="header__container pv2-video-step">
                <div className="video__container">
                    <h1 className="header__title">{title}</h1>
                    <div className="pv2-video-step__shell">
                        <p className="pv2-video-step__lead">{lead}</p>
                        {sublead ? (
                            <p className="pv2-video-step__sublead">{sublead}</p>
                        ) : null}
                        {show === true && (
                            <div
                                className={`pv2-video-step__card${
                                    started ? ' is-playing' : ' is-idle'
                                }`}
                            >
                                <ReactPlayer
                                    url={videoUrl}
                                    className="video__player"
                                    width="100%"
                                    height="100%"
                                    controls={started}
                                    playing={started && playing}
                                    playIcon={
                                        <span
                                            className="pv2-video-step__playHidden"
                                            aria-hidden="true"
                                        />
                                    }
                                    onPlay={handlePlayerPlay}
                                    onPause={() => setPlaying(false)}
                                    onDuration={handleDuration}
                                    onProgress={handleProgress}
                                    onEnded={handleEnded}
                                    progressInterval={500}
                                />
                                {!started ? (
                                    <button
                                        type="button"
                                        className="step-video-v1__idleOverlay"
                                        onClick={handleStart}
                                        aria-label="Play video to continue"
                                    >
                                        <span className="pv2-video-step__play">
                                            <span className="pv2-video-step__play-icon" />
                                        </span>
                                    </button>
                                ) : null}
                            </div>
                        )}
                    </div>
                    <div className="button__container">
                        <p className="pv2-video-step__unlock is-ready">
                            Or tap Next to continue.
                        </p>
                        <button
                            className="button__next buttonVideoNext"
                            onClick={() => changePlay()}
                            type="button"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const VideoComp = (
        <div className="header__container">
            <div className="video__container">
                <h1 className="header__title">{title}</h1>
                {show === true && (
                    <>
                        <div
                            className={`step-video-v1__card${
                                started ? ' is-playing' : ' is-idle'
                            }`}
                        >
                            <ReactPlayer
                                url={videoUrl}
                                className="video__player step-video-v1__player"
                                width="100%"
                                height="100%"
                                controls={started}
                                playing={started && playing}
                                playIcon={
                                    <span
                                        className="step-video-v1__playHidden"
                                        aria-hidden="true"
                                    />
                                }
                                onPlay={handlePlayerPlay}
                                onPause={() => setPlaying(false)}
                                onEnded={handleEnded}
                                progressInterval={500}
                                onProgress={handleProgress}
                            />
                            {!started ? (
                                <button
                                    type="button"
                                    className="step-video-v1__idleOverlay"
                                    onClick={handleStart}
                                    aria-label="Play video to continue"
                                >
                                    <span className="step-video-v1__play">
                                        <span className="step-video-v1__play-icon" />
                                    </span>
                                </button>
                            ) : null}
                        </div>
                        {!started ? (
                            <p className="step-video-v1__hint">
                                Play the video to continue.
                            </p>
                        ) : (
                            <p className="step-video-v1__hint step-video-v1__hint--ready">
                                Tap Next when you&apos;re ready.
                            </p>
                        )}
                    </>
                )}
                <div className="button__container">
                    <button
                        className={` ${
                            activeButton
                                ? ' button__next buttonVideoNext'
                                : 'btnDisableStep1'
                        }`}
                        onClick={() => changePlay()}
                        type="button"
                    >
                        NEXT
                    </button>
                </div>
            </div>
        </div>
    );
    return VideoComp;
};

export default StepHeaders;
